import React, { useMemo, useState } from 'react';
import { Medal, Download, Hammer } from 'lucide-react';

const fmtEur = (v) => `${Math.round(v || 0).toLocaleString('fr-FR')} €`;

const COLONNES = [
  { id: 'invest', label: 'Montant investi' },
  { id: 'nb', label: 'Nb améliorations' },
  { id: 'trafic', label: 'Effet trafic' },
  { id: 'caMoyen', label: 'CA moyen/j' },
];

const PODIUM_STYLE = [
  { bg: 'bg-amber-500/15 border-amber-400/60', medal: '🥇', txt: 'text-amber-300' },
  { bg: 'bg-slate-400/10 border-slate-400/50', medal: '🥈', txt: 'text-slate-300' },
  { bg: 'bg-orange-700/15 border-orange-600/50', medal: '🥉', txt: 'text-orange-300' },
];

/** Comparateur formateur — classement des équipes par investissements magasin (SCD R3). */
export default function ClassementInvestissements({ items }) {
  const [sortBy, setSortBy] = useState('invest');

  const rows = useMemo(() => {
    const playing = (items || []).filter(i => i.snapshot && (i.snapshot.total_days || 0) > 0 && i.snapshot.retail_equipements);
    return playing
      .map(({ license, snapshot }) => {
        const eq = snapshot.retail_equipements || {};
        const fx = snapshot.retail_invest_effets || {};
        const jours = snapshot.total_days || 0;
        return {
          key: license.license_key,
          equipe: license.holder_name || '—',
          entreprise: snapshot.company_name || '—',
          jours,
          invest: eq.totalInvest || 0,
          nb: eq.nbTotal || 0,
          entretienMois: eq.entretienMois || 0,
          trafic: fx.trafic_pct || 0,
          conversion: fx.conversion_pct || 0,
          caMoyen: snapshot.retail_ca_moyen_jour ?? (jours > 0 ? Math.round((snapshot.retail_ca_cumul || 0) / jours) : 0),
        };
      })
      .sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0));
  }, [items, sortBy]);

  const exportCsv = () => {
    const header = ['Rang', 'Équipe', 'Entreprise', 'Jours joués', 'Montant investi (€)', 'Nb améliorations', 'Entretien/mois (€)', 'Effet trafic (%)', 'Effet conversion (%)', 'CA moyen/jour (€)'];
    const lines = rows.map((r, i) => [i + 1, r.equipe, r.entreprise, r.jours, r.invest, r.nb, r.entretienMois, r.trafic, r.conversion, r.caMoyen]);
    const esc = (v) => {
      let s = String(v ?? '');
      if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
      return `"${s.replace(/"/g, '""')}"`;
    };
    const csv = '\ufeff' + [header, ...lines].map(l => l.map(esc).join(';')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dgs-investissements-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (rows.length === 0) return null;
  const podium = rows.slice(0, 3);

  return (
    <div className="bg-ink-800 border border-ink-600 rounded-lg p-4" data-testid="classement-investissements">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Hammer className="w-4 h-4 text-emerald-300" />
          <p className="font-bold text-sm text-slate-100">Investissements magasin par équipe</p>
          <span className="text-[10px] text-slate-500">Améliorations achetées · effets cumulés · CA moyen</span>
        </div>
        <button
          onClick={exportCsv}
          className="flex items-center gap-1.5 rounded bg-ink-700 hover:bg-ink-600 border border-ink-600 text-xs text-slate-200 px-3 py-1.5 transition-colors"
          data-testid="export-csv-invest-btn"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>

      {/* Podium visuel */}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="podium-investissements">
        {podium.map((r, i) => (
          <div key={r.key} className={`rounded-lg border p-3 ${PODIUM_STYLE[i].bg}`} data-testid={`podium-${i + 1}`}>
            <div className="flex items-center gap-2">
              <span className="text-xl">{PODIUM_STYLE[i].medal}</span>
              <div className="min-w-0">
                <p className={`font-bold text-xs truncate ${PODIUM_STYLE[i].txt}`}>{r.equipe}</p>
                <p className="text-[10px] text-slate-400 truncate">{r.entreprise}</p>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-1 font-mono text-[10px] text-slate-300">
              <p>💶 {fmtEur(r.invest)}</p>
              <p>🛠️ {r.nb} amélio.</p>
              <p>🚶 +{r.trafic} % trafic</p>
              <p>📊 {fmtEur(r.caMoyen)}/j</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-slate-500 uppercase text-[10px] border-b border-ink-600">
              <th className="py-1.5 pr-2">#</th>
              <th className="py-1.5 pr-2">Équipe</th>
              {COLONNES.map((c) => (
                <th key={c.id} className="py-1.5 pr-2">
                  <button
                    onClick={() => setSortBy(c.id)}
                    className={`uppercase text-[10px] transition-colors ${sortBy === c.id ? 'text-emerald-300 font-bold' : 'hover:text-slate-300'}`}
                    data-testid={`sort-invest-${c.id}`}
                  >
                    {c.label}{sortBy === c.id ? ' ▼' : ''}
                  </button>
                </th>
              ))}
              <th className="py-1.5 pr-2">Effet conv.</th>
              <th className="py-1.5">Entretien/mois</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.key} className="border-b border-ink-700/60" data-testid={`invest-row-${r.key}`}>
                <td className="py-1.5 pr-2 font-mono">{i < 3 ? PODIUM_STYLE[i].medal : i + 1}</td>
                <td className="py-1.5 pr-2 font-bold text-slate-200">{r.equipe}</td>
                <td className="py-1.5 pr-2 font-mono font-bold text-emerald-200">{fmtEur(r.invest)}</td>
                <td className="py-1.5 pr-2 font-mono text-slate-300">{r.nb}</td>
                <td className="py-1.5 pr-2 font-mono text-slate-300">+{r.trafic} %</td>
                <td className="py-1.5 pr-2 font-mono text-slate-300">{fmtEur(r.caMoyen)}</td>
                <td className="py-1.5 pr-2 font-mono text-slate-400">+{r.conversion} %</td>
                <td className="py-1.5 font-mono text-slate-400">{fmtEur(r.entretienMois)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[10px] text-slate-500 italic flex items-center gap-1">
        <Medal className="w-3 h-3" /> Lecture pédagogique : comparez le montant investi à l'effet obtenu (trafic/conversion) et au CA moyen — investir beaucoup n'est rentable que si le flux suit.
      </p>
    </div>
  );
}
